import { fieldSizeValidation, useField, useSaveDetail } from '@cibo/landmanager'
import {
  GeometryEditor,
  GeometryEditorContext,
  LayerProvider,
  useGeometryEditorStateContext,
} from '@cibo/mapgl'
import { ActionBar } from '@cibo/ui'
import { LoadingButton } from '@mui/lab'
import { Alert, Button, Stack, Tooltip, Typography } from '@mui/material'
import { featureCollection, union } from '@turf/turf'
import { useSnackbar } from 'notistack'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

export const BoundaryEditor = () => {
  const { resourceId } = useParams()

  return (
    <GeometryEditorContext resourceId={resourceId}>
      <LayerProvider>
        <BoundaryEditorImpl />
      </LayerProvider>
    </GeometryEditorContext>
  )
}

export const BoundaryEditorImpl = () => {
  const { t } = useTranslation('impact/pages/BoundaryEditor')
  const navigate = useNavigate()
  const saveDetail = useSaveDetail()
  const { enqueueSnackbar } = useSnackbar()
  const {
    touched,
    features,
    resourceId,
    editorOverlaps,
    setBaseFeatureCollection,
  } = useGeometryEditorStateContext()
  const fieldQuery = useField(resourceId)
  const { acres, errorCodes, errorMessages } = fieldSizeValidation(features)
  const immutable = fieldQuery.data?.resolveStandingDetail('geometry').immutable

  useEffect(() => {
    setBaseFeatureCollection(
      fieldQuery.data?.feature
        ? featureCollection([fieldQuery.data.feature])
        : featureCollection([])
    )
  }, [fieldQuery.data?.feature])

  const handleSaveGeometry = () => {
    if (resourceId) {
      try {
        //@ts-ignore
        const combinedFeature = features.features.reduce(union)

        saveDetail.mutateAsync(
          {
            resourceId,
            details: [
              {
                traitId: 'geometry',
                input: {
                  feature: combinedFeature,
                },
              },
            ],
          },
          {
            onSuccess: () => {
              enqueueSnackbar(t('geometrySave', { context: 'onSuccess' }), { variant: 'success' })
              navigate('../')
            },
            onError: () => {
              enqueueSnackbar(
                `${t('geometrySave', { context: 'onError' })}: ${JSON.stringify(
                  // @ts-ignore saveDetail is not appropriately typed
                  saveDetail.error?.message
                )}`,
                {
                  variant: 'error',
                }
              )
            },
          }
        )
      } catch (err) {
        console.log(err)
      }
    }
  }

  const hasOverlaps = !!editorOverlaps.overlaps?.length

  const disableSave =
    !touched || errorCodes.length > 0 || features.features.length === 0 || immutable || hasOverlaps

  return (
    <>
      <Stack flex={1}>
        <GeometryEditor disabled={!fieldQuery.data?.can(['edit_land']) || immutable} />
      </Stack>
      <ActionBar>
        <Stack direction="row" flexWrap="wrap" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={3} flexWrap="wrap" alignItems="center">
            {fieldQuery.data && (
              <>
                {acres !== 0 && (
                  <Typography variant="body2" color="text.secondary">
                    {t('acres', { count: acres })}
                  </Typography>
                )}
                {errorCodes.map((code, i) => (
                  <Tooltip key={code} title={errorMessages[i]}>
                    <Typography variant="caption" color="error">
                      {t(code)}
                    </Typography>
                  </Tooltip>
                ))}
                {features.features.length === 0 && (
                  <Typography variant="caption" color="error">
                    {t('noAcres')}
                  </Typography>
                )}
                {hasOverlaps && (
                  <Typography variant="caption" color="error">
                    {t('overlaps')}
                  </Typography>
                )}
              </>
            )}
          </Stack>
          {!!immutable && (
            <Alert severity="error">
              {t('immutable', { count: fieldQuery.data?.commitments.length })}
            </Alert>
          )}
          <Stack direction="row" alignItems="center" spacing={1}>
            <Button onClick={() => navigate('../')} variant="text" disabled={saveDetail.isLoading}>
              {t('cancel')}
            </Button>
            <LoadingButton
              variant="contained"
              onClick={handleSaveGeometry}
              disabled={disableSave}
              loading={saveDetail.isLoading}
            >
              {t('save')}
            </LoadingButton>
          </Stack>
        </Stack>
      </ActionBar>
    </>
  )
}
